import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateWorkflowInput, CreateWorkflowInputSchema } from '@idc-forms/schemas'
import { ArrowDown, ArrowUp, X } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useStatesLookupQuery } from '@/features/states/hooks/use-states-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { CATLOG_ACTION_WORKFLOW } from '../lib/consts'

type WorkflowFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateWorkflowInput>
  /** El flujo default no puede cambiar de acción (el backend responde 400). */
  lockActions?: boolean
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateWorkflowInput) => void
  onCancel: () => void
}

/**
 * Formulario de flujo de trabajo (crear/editar) para el side panel. RHF +
 * zodResolver con el MISMO schema (@idc-forms/schemas) que valida el backend.
 * La secuencia de estados es ORDENADA: el orden visual es el orden real (el
 * server recalcula `order` por posición). Solo estados `process` entran al
 * flujo.
 */
export function WorkflowForm({
  defaultValues,
  lockActions = false,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: WorkflowFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateWorkflowInput>({
    resolver: zodResolver(CreateWorkflowInputSchema),
    defaultValues: {
      actions: ['new'],
      states: [],
      default: false,
      active: true,
      ...defaultValues
    }
  })

  const { data: statesLookup = [] } = useStatesLookupQuery()
  const processStates = statesLookup.filter((state) => state.type === 'process')
  const stateName = (id: string) =>
    statesLookup.find((state) => state._id === id)?.name ?? 'Estado eliminado'

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldSet className="gap-4">
        <FieldLegend>Identificación</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.name)}>
                <FieldLabel htmlFor="workflow-name">Nombre</FieldLabel>
                <Input
                  id="workflow-name"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldDescription>Único entre todos los flujos de trabajo.</FieldDescription>
                <FieldError errors={[errors.name]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.description)}>
                <FieldLabel htmlFor="workflow-description">Descripción</FieldLabel>
                <Input
                  id="workflow-description"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  autoComplete="off"
                />
                <FieldError errors={[errors.description]} />
              </Field>
            )}
          />

          <Controller
            control={control}
            name="actions"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.actions)}>
                <FieldLabel htmlFor="workflow-action">Acción</FieldLabel>
                <NativeSelect
                  id="workflow-action"
                  disabled={lockActions}
                  value={field.value?.[0] ?? ''}
                  onChange={(e) => {
                    field.onChange(e.target.value ? [e.target.value] : [])
                  }}
                >
                  <NativeSelectOption value="" disabled>
                    Selecciona una acción
                  </NativeSelectOption>
                  {CATLOG_ACTION_WORKFLOW.map((action) => (
                    <NativeSelectOption key={action.value} value={action.value}>
                      {action.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
                {lockActions && (
                  <FieldDescription>
                    El flujo predeterminado de una acción no puede cambiar de acción.
                  </FieldDescription>
                )}
                <FieldError errors={[errors.actions as { message?: string } | undefined]} />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Secuencia de estados</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="states"
            render={({ field }) => {
              const steps = field.value ?? []
              const selectedIds = new Set(steps.map((step) => step.state))
              const available = processStates.filter((state) => !selectedIds.has(state._id))

              const move = (index: number, delta: number) => {
                const next = [...steps]
                const target = index + delta
                if (target < 0 || target >= next.length) return
                ;[next[index], next[target]] = [next[target], next[index]]
                field.onChange(next)
              }

              return (
                <Field data-invalid={Boolean(errors.states)}>
                  <FieldLabel htmlFor="workflow-add-state">Agregar estado</FieldLabel>
                  <NativeSelect
                    id="workflow-add-state"
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return
                      field.onChange([...steps, { state: e.target.value }])
                    }}
                  >
                    <NativeSelectOption value="">
                      {available.length ? 'Selecciona un estado…' : 'Sin estados disponibles'}
                    </NativeSelectOption>
                    {available.map((state) => (
                      <NativeSelectOption key={state._id} value={state._id}>
                        {state.name}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  <FieldDescription>
                    El orden de la lista es el orden real del flujo.
                  </FieldDescription>

                  {steps.length > 0 && (
                    <ol className="mt-1 flex flex-col gap-1">
                      {steps.map((step, index) => (
                        <li
                          key={step.state}
                          className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-2 py-1"
                        >
                          <span className="w-5 shrink-0 text-center text-xs font-medium tabular-nums text-muted-foreground">
                            {index + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm">
                            {stateName(step.state ?? '')}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Subir estado"
                            disabled={index === 0}
                            onClick={() => {
                              move(index, -1)
                            }}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Bajar estado"
                            disabled={index === steps.length - 1}
                            onClick={() => {
                              move(index, 1)
                            }}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            aria-label="Quitar estado"
                            onClick={() => {
                              field.onChange(steps.filter((_, i) => i !== index))
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      ))}
                    </ol>
                  )}
                  <FieldError errors={[errors.states as { message?: string } | undefined]} />
                </Field>
              )
            }}
          />
        </FieldGroup>
      </FieldSet>

      <FieldSet className="gap-4">
        <FieldLegend>Estado</FieldLegend>
        <FieldGroup className="gap-4">
          <Controller
            control={control}
            name="default"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="workflow-default">Predeterminado</FieldLabel>
                <Switch
                  id="workflow-default"
                  checked={field.value ?? false}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
          <FieldDescription>
            Solo puede haber un flujo predeterminado por acción: marcarlo desmarca el actual.
          </FieldDescription>

          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <Field orientation="horizontal">
                <FieldLabel htmlFor="workflow-active">Activo</FieldLabel>
                <Switch
                  id="workflow-active"
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </FieldSet>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
